import { useEffect, useState, type FormEvent } from "react";
import { asDecimalString } from "@shop-ledger/shared-types";
import { Button, DataTable, Input, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createStockTransfer, listStockTransfers } from "../../api/stockTransfersApi";
import { listInventory } from "../../api/inventoryApi";
import type { InventoryItemOut, StockTransferOut } from "../../api/types";
import { BranchSelect } from "../../components/BranchSelect";
import { ApiError } from "../../lib/apiClient";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";

const COLUMNS: DataTableColumn<StockTransferOut, keyof StockTransferOut>[] = [
  { key: "inventory_item_id", header: "Item", render: (r) => <span className="muted">{r.inventory_item_id.slice(0, 8)}</span> },
  { key: "from_branch_id", header: "From", render: (r) => <span className="muted">{r.from_branch_id.slice(0, 8)}</span> },
  { key: "to_branch_id", header: "To", render: (r) => <span className="muted">{r.to_branch_id.slice(0, 8)}</span> },
  { key: "quantity", header: "Quantity", align: "right", render: (r) => r.quantity },
  { key: "status", header: "Status", render: (r) => <span className="badge badge--current">{r.status}</span> },
];

/**
 * Inter-branch stock transfers — `POST/GET /stock-transfers` (§10.4). Atomic on the
 * backend: guarded source decrement + destination increment/create, fails fast (409) with
 * no partial transfer if source stock is insufficient.
 */
export function StockTransfersPage() {
  const { ensureFreshToken } = useAuth();
  const transfersState = useAsyncData(() => ensureFreshToken().then((token) => listStockTransfers(token)), []);

  const [fromBranchId, setFromBranchId] = useState("");
  const [toBranchId, setToBranchId] = useState("");
  const [itemId, setItemId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [items, setItems] = useState<InventoryItemOut[]>([]);
  useEffect(() => {
    if (!fromBranchId) {
      setItems([]);
      setItemId("");
      return;
    }
    let cancelled = false;
    ensureFreshToken()
      .then((token) => listInventory(token, fromBranchId))
      .then((result) => {
        if (!cancelled) setItems(result);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [fromBranchId, ensureFreshToken]);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    if (fromBranchId && fromBranchId === toBranchId) {
      setFormError("Source and destination branches must differ.");
      return;
    }
    if (!itemId) {
      setFormError("Select an item to transfer.");
      return;
    }
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await createStockTransfer(token, {
        from_branch_id: fromBranchId,
        to_branch_id: toBranchId,
        inventory_item_id: itemId,
        quantity: asDecimalString(quantity),
      });
      setQuantity("");
      transfersState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not create stock transfer.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Stock Transfers</h1>
          <p className="page__subtitle">Move stock between branches — atomic: fails fast with no partial transfer if source stock is insufficient.</p>
        </div>
      </div>

      {transfersState.error ? <StatusBanner kind="error">{transfersState.error}</StatusBanner> : null}

      <div className="panel">
        <h2 className="panel__title">New transfer</h2>
        <form onSubmit={handleCreate}>
          <div className="form-grid">
            <BranchSelect id="transfer-from" label="From branch" value={fromBranchId} onChange={setFromBranchId} required />
            <BranchSelect id="transfer-to" label="To branch" value={toBranchId} onChange={setToBranchId} required />
            <div className="sl-field">
              <label className="sl-field__label" htmlFor="transfer-item">
                Item (from source branch's stock)
              </label>
              <select id="transfer-item" className="sl-input" value={itemId} onChange={(e) => setItemId(e.target.value)} disabled={!fromBranchId} required>
                <option value="">Select item…</option>
                {items.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.sku} — {item.name} (stock: {item.current_stock})
                  </option>
                ))}
              </select>
            </div>
            <Input label="Quantity" inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} required />
          </div>
          {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
          <div className="form-actions">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Transferring…" : "Transfer stock"}
            </Button>
          </div>
        </form>
      </div>

      <div className="panel">
        {transfersState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable columns={COLUMNS} rows={transfersState.data ?? []} getRowId={(row) => row.id} emptyMessage="No transfers yet." caption="Stock Transfers" />
        )}
      </div>
    </div>
  );
}
